import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import Footer from './Footer';
import LanguageSelector from './LanguageSelector';

const PrivacyPolicy: React.FC = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-orange-light-gradient">
      {/* Header */}
      <nav className="bg-white shadow-orange border-b border-orange-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <Link to={`/${language}`} className="text-xl sm:text-2xl font-bold text-orange-gradient flex-shrink-0 hover:opacity-80 transition-opacity">
              {t.nav.title}
            </Link>
            <LanguageSelector />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl p-6 sm:p-8 lg:p-12 shadow-orange">
          <h1 className="text-3xl sm:text-4xl font-bold text-orange-gradient mb-8">Privacy Policy</h1>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p className="text-lg text-gray-600 mb-8">
              <strong>Last updated:</strong> December 2024
            </p>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Introduction</h2>
              <p>
                Welcome to BG Remover ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, and protect your information when you use our background removal service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Images and Files</h3>
              <p>
                <strong>Local Processing Only:</strong> All images you upload are processed entirely on your device using client-side technology. Your images are never uploaded to our servers or transmitted over the internet.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.2 Technical Information</h3>
              <p>We may collect basic technical information including:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Browser type and version</li>
                <li>Device type and operating system</li>
                <li>Language preferences</li>
                <li>General usage statistics (anonymized)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">3. How We Use Your Information</h2>
              <p>We use the collected information to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Provide and improve our background removal service</li>
                <li>Optimize performance and user experience</li>
                <li>Analyze usage patterns to enhance our features</li>
                <li>Ensure technical functionality and security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">4. Data Storage and Security</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Local Storage</h3>
              <p>
                Your images are processed locally in your browser and are automatically deleted when you close the browser tab or navigate away from the page. We do not store your images on our servers.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2 Browser Storage</h3>
              <p>
                We may use local storage and cookies to save your language preferences and improve your user experience. This information remains on your device and can be cleared at any time through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Third-Party Services</h2>
              <p>
                Our service may use third-party analytics tools to help us understand how users interact with our website. These services may collect anonymized usage data. We ensure that any third-party services we use comply with privacy standards and do not have access to your images.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Clear your browser data and local storage at any time</li>
                <li>Disable cookies through your browser settings</li>
                <li>Request information about data we may have collected</li>
                <li>Contact us with any privacy-related questions or concerns</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Children's Privacy</h2>
              <p>
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-orange-50 p-4 rounded-lg mt-4 border border-orange-200">
                <p><strong>Email:</strong> <a href="mailto:contact@waicto.com" className="text-orange-600 hover:text-orange-700">contact@waicto.com</a></p>
                <p><strong>Website:</strong> <a href="https://waicto.com" className="text-orange-600 hover:text-orange-700">waicto.com</a></p>
              </div>
            </section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-center text-gray-600">
                <Link to={`/${language}`} className="text-orange-600 hover:text-orange-700 font-medium">
                  ← Back to BG Remover
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;